import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError, delay, mergeMap, retryWhen} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {Inject, Injectable} from '@angular/core';
import {RollbarService} from '../services/rollbar.service';
import Rollbar from 'rollbar';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

  private static DEFAULT_ERROR_MESSAGE = 'Could not connect to the server';
  private static DEFAULT_MAX_RETRIES = 0;
  private static DEFAULT_BACKOFF_MS = 1000;

  constructor(@Inject(RollbarService) private rollbar: Rollbar) {
  }
  static getErrorMessage(errorMessage: string): string {
    return (!environment.production) ? errorMessage : HttpErrorInterceptor.DEFAULT_ERROR_MESSAGE;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const retryCount = request.method === 'GET' ? 2 : 0;
    return next.handle(request)
      .pipe(
        this.retryWithBackoff(1000, retryCount),
        catchError((error: HttpErrorResponse) => this.handleErrorNew(error))
      );
  }

  private handleErrorNew(error: HttpErrorResponse) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    if (environment.production) {
      this.rollbar.error(error);
    }

    // For validation errors (400), preserve the original HttpErrorResponse
    // so that API validation errors can be mapped to form controls
    if (error.status === 400 && error.error?.errors) {
      return throwError(() => error);
    }

    return throwError(() => HttpErrorInterceptor.getErrorMessage(errorMessage));
  }

  private retryWithBackoff(delayMs: number, maxRetry = HttpErrorInterceptor.DEFAULT_MAX_RETRIES, backoffMs = HttpErrorInterceptor.DEFAULT_BACKOFF_MS): (src: Observable<any>) => Observable<any> {
    let retries = maxRetry;
    return (src: Observable<any>) =>
      src.pipe(
        retryWhen((errors: Observable<any>) => errors.pipe(
          mergeMap(error => {
              // Don't retry client errors (4xx) — they won't resolve with retries
              if (error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500) {
                return throwError(error);
              }
              if (retries-- > 0) {
                const backoffTime = delayMs + (maxRetry - retries) * backoffMs;
                return of(error).pipe(delay(backoffTime));
              } else {
                return throwError(error);
              }
            }
          ))));
  }
}
